import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  LogOut,
  Tag,
  Shield,
  Mail,
  UserCog,
  MailQuestion,
  X,
  Info,
  FileText,
  ChevronDown,
  MessageSquare,
  LayoutGrid,
  Layers,
  Database,
  Menu,
} from "lucide-react";
import { LogoIcon } from "../Icons";
import useAuthStore from "../../store/authStore";

// ─── Collapsible Group ────────────────────────────────────────────────────────
const SidebarGroup = ({ label, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="space-y-1">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-6 py-2 group"
      >
        <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
          {label}
        </p>
        <ChevronDown
          className={`w-3 h-3 text-slate-600 transition-transform duration-200 ${
            open ? "rotate-0" : "-rotate-90"
          }`}
        />
      </button>
      {open && <div className="space-y-1">{children}</div>}
    </div>
  );
};

// Items carry a `minLevel`: the nav entry is visible only when the current
// user's role level is <= minLevel (lower level = more privilege).
const AdminSidebar = ({ isOpen, setIsOpen, onLogout, activeTab }) => {
  const navigate = useNavigate();
  const userLevel = useAuthStore((s) => s.user?.roleLevel);
  const effectiveLevel = typeof userLevel === "number" ? userLevel : 3;
  const canAccess = (minLevel) => effectiveLevel <= minLevel;

  const mainMenuItems = [
    { icon: LayoutDashboard, label: "Dashboard", id: "dashboard", minLevel: 3 },
    { icon: Users, label: "Submissions", id: "submissions", minLevel: 3 },
    { icon: MailQuestion, label: "Inquiries", id: "inquiries", minLevel: 3 },
  ];

  const contentItems = [
    { icon: Layers, label: "Page Sections", id: "page-sections", minLevel: 2 },
    { icon: MessageSquare, label: "Testimonials", id: "testimonials", minLevel: 2 },
    { icon: LayoutGrid, label: "Service Cards", id: "service-cards", minLevel: 2 },
    { icon: Database, label: "Content Blocks", id: "content-blocks", minLevel: 2 },
    { icon: Menu, label: "Navigation Items", id: "navigation-items", minLevel: 0 },
    { icon: Info, label: "Site Information", id: "info", minLevel: 2 },
    { icon: FileText, label: "Content Pages", id: "content-pages", minLevel: 2 },
  ];

  const systemItems = [
    { icon: Tag, label: "Manage Tags", id: "tags", minLevel: 1 },
    { icon: Shield, label: "Manage Roles", id: "roles", minLevel: 1 },
    { icon: UserCog, label: "Admin Users", id: "users", minLevel: 1 },
    { icon: Mail, label: "Email Templates", id: "emails", minLevel: 1 },
  ];

  const visibleMain = mainMenuItems.filter((i) => canAccess(i.minLevel));
  const visibleContent = contentItems.filter((i) => canAccess(i.minLevel));
  const visibleSystem = systemItems.filter((i) => canAccess(i.minLevel));

  const NavButton = ({ item }) => {
    const isActive = activeTab === item.id;
    return (
      <button
        key={item.id}
        onClick={() => {
          navigate(`/admin/${item.id}`);
          setIsOpen(false);
        }}
        className={`w-full flex items-center space-x-4 px-6 py-3 rounded-2xl font-bold transition-all ${
          isActive
            ? "bg-white text-subidha-blue font-black shadow-lg shadow-blue-500/10"
            : "text-slate-400 hover:bg-white/5 hover:text-white"
        }`}
      >
        <item.icon className="w-5 h-5" />
        <span className="text-sm tracking-wide">{item.label}</span>
      </button>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      <div
        className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
      />

      <aside
        className={`
        fixed left-0 top-0 h-screen bg-[#0F172A] text-white w-72 z-50
        transition-transform duration-300 ease-in-out flex flex-col shadow-2xl
        ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      >
        <div className="p-8 flex items-center justify-between">
          <Link to="/" onClick={() => setIsOpen(false)}>
            <LogoIcon className="h-9 w-auto rounded-md" />
          </Link>
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-2 text-slate-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <nav className="flex-1 px-4 space-y-6 overflow-y-auto custom-scrollbar pb-4">
          {/* Main Menu */}
          <SidebarGroup label="Main Menu" defaultOpen={true}>
            {visibleMain.map((item) => (
              <NavButton key={item.id} item={item} />
            ))}
          </SidebarGroup>

          {/* Content Management */}
          {visibleContent.length > 0 && (
            <SidebarGroup label="Content Management" defaultOpen={true}>
              {visibleContent.map((item) => (
                <NavButton key={item.id} item={item} />
              ))}
            </SidebarGroup>
          )}

          {/* System Management */}
          {visibleSystem.length > 0 && (
            <SidebarGroup label="System Management" defaultOpen={false}>
              {visibleSystem.map((item) => (
                <NavButton key={item.id} item={item} />
              ))}
            </SidebarGroup>
          )}
        </nav>

        <div className="p-6 border-t border-white/5 mt-auto">
          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-4 px-6 py-4 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white rounded-2xl font-black transition-all group"
          >
            <LogOut className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            <span className="text-xs uppercase tracking-widest">
              Logout Account
            </span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
